// src/utils/env.js
/**
 * Environment configuration utility
 * Handles process.env safely to avoid "process is not defined" errors
 */

// Import process from our polyfill (if available)
import processPolyfill from './processPolyfill';

// Define default values for env variables
const defaultEnv = {
  NODE_ENV: 'development',
  REACT_APP_API_BASE_URL: '',
  // Add other environment variables your app uses
};

// Safely get the window object
const getGlobalObject = () => {
  if (typeof window !== 'undefined') return window;
  if (typeof global !== 'undefined') return global;
  // Remove reference to 'self' to fix ESLint warning
  return {};
};

// Create a safe environment object with more robust checks
const getSafeEnv = () => {
  // First try our polyfill
  if (processPolyfill && processPolyfill.env) {
    return processPolyfill.env;
  }
  
  // Then check if process exists in the current context
  if (typeof process !== 'undefined' && process && process.env) {
    return process.env;
  }
  
  // If not, check if it was injected into window by webpack
  const globalObj = getGlobalObject();
  if (globalObj.process && globalObj.process.env) {
    return globalObj.process.env;
  }
  
  // If still not found, use default values
  return { ...defaultEnv };
};

// Get the environment object safely
const env = getSafeEnv();

/**
 * Get environment variable with fallback
 * @param {string} key - The environment variable name
 * @param {string} defaultValue - Fallback value if not defined
 * @returns {string} The environment value
 */
export const getEnv = (key, defaultValue = '') => {
  return (env && env[key]) || defaultValue;
};

/**
 * Check if running in production mode
 */
export const isProduction = getEnv('NODE_ENV') === 'production';

/**
 * Check if running in development mode
 */
export const isDevelopment = getEnv('NODE_ENV') === 'development' || !getEnv('NODE_ENV');

// For direct mapping process.env access patterns
export const processEnv = env;

export default {
  getEnv,
  isProduction,
  isDevelopment,
  processEnv
};